import {
  BillingInformation,
  useUpdateBillingAdminInstruction,
  useValidateBillingInformation,
} from '@app/api';
import { SubmitButton, useToast } from '@app/core';
import { Theme } from '@app/theme';
import { SubmitButtonsLine, TextArea, Toggle, Select } from '@app/form';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { ApiValidationFailedException } from 'store/modules/exceptions';
import * as Yup from 'yup';
import { ExtraBillingEmailsField } from './ExtraBillingEmailsField';

interface Props {
  billingInformation: BillingInformation;
  onConfirm?: (billingInformation: BillingInformation) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h4,
    marginBottom: theme.marginBase * 2,
  },
  buttons: {
    marginTop: theme.marginBase * 4,
  },
}));

export const validationSchema = Yup.object().shape({
  billingInstructions: Yup.string().nullable(),
  preventAutomaticBilling: Yup.boolean(),
  requirePurchaseOrderNumber: Yup.boolean(),
  requireUseOfSpecificPlatform: Yup.boolean(),
  extraBillingEmails: Yup.array(Yup.string().email()).nullable(),
  locale: Yup.string(),
});

export const FinalBillingInformationVerificationStep = ({
  billingInformation,
  onConfirm,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();

  const { mutateAsync: updateInfos, isLoading: isSaving } =
    useUpdateBillingAdminInstruction(billingInformation.id);
  const { mutateAsync: validate, isLoading: isValidating } =
    useValidateBillingInformation(billingInformation.id);

  const initialValues = useMemo(
    (): BillingInformation => ({
      ...billingInformation,
      extraBillingEmails: billingInformation.extraBillingEmails || [],
      billingInstructions: '',
    }),
    [billingInformation],
  );

  const onSubmit = async (
    values: BillingInformation,
    { setStatus }: FormikHelpers<BillingInformation>,
  ) => {
    try {
      await updateInfos(values);
      const validated = await validate();
      toast.saved();
      if (onConfirm) {
        onConfirm(validated);
      }
    } catch (e) {
      toast.exception(e);
      if (e instanceof ApiValidationFailedException) {
        setStatus(e.getFormikErrors());
      }
    }
  };

  const localeOptions = useMemo(() => {
    if (billingInformation.provider === 'SEVDESK') {
      return [
        {
          value: 'de',
          label: t(
            'admin.billing.billingInfo.finalVerification.localeOptions.de',
          ),
        },
        {
          value: 'en',
          label: t(
            'admin.billing.billingInfo.finalVerification.localeOptions.en',
          ),
        },
      ];
    }
    return [
      {
        value: 'fr',
        label: t(
          'admin.billing.billingInfo.finalVerification.localeOptions.fr',
        ),
      },
      {
        value: 'en',
        label: t(
          'admin.billing.billingInfo.finalVerification.localeOptions.en',
        ),
      },
    ];
  }, [billingInformation.provider, t]);

  return (
    <div>
      <h2 className={classes.title}>
        {t('admin.billing.billingInfo.finalVerification.title')}
      </h2>

      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <Form>
          <Toggle
            name="preventAutomaticBilling"
            label={t(
              'admin.billing.billingInfo.finalVerification.preventAutomaticBilling',
            )}
          />
          <TextArea
            name="billingInstructions"
            label={t(
              'admin.billing.billingInfo.finalVerification.billingInstructions',
            )}
            rows={5}
          />
          <Toggle
            name="requirePurchaseOrderNumber"
            label={t(
              'admin.billing.billingInfo.finalVerification.requirePurchaseOrderNumber',
            )}
          />
          <Toggle
            name="requireUseOfSpecificPlatform"
            label={t(
              'admin.billing.billingInfo.finalVerification.requireUseOfSpecificPlatform',
            )}
          />

          <ExtraBillingEmailsField
            name="extraBillingEmails"
            label={t(
              'admin.billing.billingInfo.finalVerification.extraBillingEmails',
            )}
          />

          <Select
            name="locale"
            label={t('admin.billing.billingInfo.finalVerification.locale')}
            options={localeOptions}
          />

          <SubmitButtonsLine className={classes.buttons}>
            <SubmitButton
              small
              loading={isSaving || isValidating}
              text={t('admin.billing.billingInfo.finalVerification.confirm')}
            />
          </SubmitButtonsLine>
        </Form>
      </Formik>
    </div>
  );
};
